import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { documentsAPI } from '../api/client';
import { extractErrorMessage, quotaFromError } from '../utils/errors';
import QuotaLimitModal from './QuotaLimitModal';

/**
 * "Do we already have this?" and "what could this reuse?" (GitHub issue #35,
 * use cases 1 and 4).
 *
 * Advisory only -- nothing here blocks anything, and this component never
 * implies otherwise.
 *
 * THE DISTINCTION THIS PANEL HAS TO GET RIGHT, and the reason it does not
 * simply copy TypeEvaluationPanel: a corpus scan is a REAL, COMPLETE result
 * with no model involved. The matches and their similarity percentages come
 * from services/corpus_service.py, deterministic application code. So the
 * absence of a narrative is an OFFER ("explain these with AI"), never a
 * warning that nothing happened -- which is what `is_stub` means and is a
 * different state entirely.
 *
 * Model output (the prose appended to a match's reason line, and the
 * recommendations list) renders as plain React TEXT NODES ONLY -- no
 * dangerouslySetInnerHTML, no RichContentView, no RedlineView. Same rule as
 * TypeEvaluationPanel.
 *
 * A `redacted` match is one the VIEWER may not read, filtered server-side per
 * reader (see routers/documents.py::_readable_related_ids). It still occupies
 * a row, because "there is another match you cannot see" is honest and useful,
 * but it carries no title and no id and is never a link.
 */

const KIND_LABELS = {
  duplicate_scan: { icon: '⧉', title: 'Similar Existing Documents' },
  reuse_candidates: { icon: '♻️', title: 'Reuse Opportunities' },
  // Issue #68 (#35's use case 2). A heading-set diff against the document's
  // current type's best-fitting active template -- see
  // services/template_conformance_service.py.
  template_conformance: { icon: '📋', title: 'Template Conformance' },
};

const FINDING_LABELS = {
  duplicate_candidate: { text: 'Possible duplicate', variant: 'warning' },
  similar_document: { text: 'Related', variant: 'neutral' },
  reuse_candidate: { text: 'Could reuse', variant: 'info' },
  missing_section: { text: 'Missing section', variant: 'warning' },
};

// COMPLIANCE_COLORS mirrors DraftingAssistantPanel's compliance badge exactly
// -- only template_conformance ever sets this field, but the shape is
// app-wide, so the same three colours read the same way everywhere.
const COMPLIANCE_COLORS = {
  compliant: { background: 'var(--success-bg)', color: 'var(--success-ink)' },
  partial: { background: 'var(--warning-bg)', color: 'var(--warning-ink)' },
  non_compliant: { background: 'var(--danger-bg)', color: 'var(--danger-ink)' },
  indeterminate: { background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' },
};

function MatchRow({ candidate }) {
  const label = FINDING_LABELS[candidate.finding_type] || FINDING_LABELS.similar_document;
  // A missing-section row (issue #68) has no document and no similarity
  // figure -- candidate.title IS the section heading, not a document title,
  // and there is nothing to link to.
  const isSection = candidate.finding_type === 'missing_section';
  const percent = candidate.similarity != null ? Math.round(candidate.similarity * 100) : null;

  return (
    <li style={{ marginBottom: '10px', listStyle: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
        <span
          className="badge"
          style={{
            background: `var(--${label.variant}-bg)`,
            color: `var(--${label.variant}-ink)`,
          }}
        >
          {label.text}
        </span>
        {candidate.redacted ? (
          <span style={{ fontSize: '13.5px', color: 'var(--muted)', fontStyle: 'italic' }}>
            A document in a location you cannot access
          </span>
        ) : isSection ? (
          <span style={{ fontSize: '13.5px' }}>{candidate.title}</span>
        ) : (
          <Link to={`/documents/${candidate.document_id}`} style={{ fontSize: '13.5px' }}>
            {candidate.title}
          </Link>
        )}
        {percent != null && (
          <span style={{ fontSize: '12.5px', color: 'var(--muted)' }}>{percent}% similar</span>
        )}
      </div>
      {!candidate.redacted && candidate.body && (
        <div style={{ fontSize: '12.5px', color: 'var(--ink-soft)', marginTop: '2px' }}>
          {candidate.body}
        </div>
      )}
    </li>
  );
}

function CorpusScanPanel({
  documentId,
  revisionId = null,
  kind = 'duplicate_scan',
  canRun = false,
}) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [quotaError, setQuotaError] = useState(null); // issue #159

  const meta = KIND_LABELS[kind] || KIND_LABELS.duplicate_scan;

  const fetchLatest = useCallback(async () => {
    try {
      const res = await documentsAPI.getCorpusScan(documentId, { kind, revisionId });
      setScan(res.data);
    } catch (err) {
      // Non-fatal and deliberately silent, same as TypeEvaluationPanel: not
      // being able to load this must never stop the page around it rendering.
      console.error('Error loading corpus scan:', err);
      setScan(null);
    } finally {
      setLoading(false);
    }
  }, [documentId, revisionId, kind]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  const run = async ({ withNarrative }) => {
    setRunning(true);
    setError(null);
    try {
      const res = await documentsAPI.runCorpusScan(documentId, {
        kind,
        revisionId,
        force: true,
        withNarrative,
      });
      setScan(res.data);
    } catch (err) {
      console.error('Error running corpus scan:', err);
      const quota = quotaFromError(err);
      if (quota) setQuotaError(quota);
      else setError(extractErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header"><h3>{meta.icon} {meta.title}</h3></div>
        <div className="loading">Loading…</div>
      </div>
    );
  }

  const candidates = scan?.candidates || [];

  return (
    <div className="card">
      <div className="card-header">
        <h3>{meta.icon} {meta.title}</h3>
        {canRun && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => run({ withNarrative: false })}
              disabled={running}
            >
              {running ? '⏳ Working…' : scan ? '🔄 Re-scan' : '▶️ Scan corpus'}
            </button>
            {scan && candidates.length > 0 && !scan.has_narrative && (
              <button
                className="btn btn-secondary btn-small"
                onClick={() => run({ withNarrative: true })}
                disabled={running}
              >
                🤖 Explain with AI
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      <QuotaLimitModal quota={quotaError} onClose={() => setQuotaError(null)} />

      {!scan ? (
        <div style={{ color: 'var(--muted)', fontSize: '13.5px' }}>
          {kind === 'template_conformance'
            ? (canRun
              ? 'Not scanned yet — check this document against its type’s template.'
              : 'This document has not been checked against its type’s template.')
            : (canRun
              ? 'Not scanned yet — check whether this document overlaps anything already '
                + 'in the library.'
              : 'This document has not been compared against the library.')}
        </div>
      ) : (
        <>
          {scan.is_stub && (
            <div className="alert alert-warning" style={{ fontSize: '12.5px' }}>
              The written explanation below was generated without a language model and is
              not a real assessment. The matches and their scores are unaffected — those
              are calculated by the application.
            </div>
          )}

          {scan.compliance_status && (
            <span
              className="badge"
              style={COMPLIANCE_COLORS[scan.compliance_status] || COMPLIANCE_COLORS.indeterminate}
            >
              {scan.compliance_status.replace('_', ' ')}
            </span>
          )}

          {candidates.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: '13.5px', marginTop: '8px' }}>
              {kind === 'template_conformance'
                ? 'Every section in the template is present in this document. '
                  + 'This is a result, not a failure to run.'
                : 'No documents in the library resembled this one closely enough to report. '
                  + 'This is a result, not a failure to run.'}
            </div>
          ) : (
            <>
              <div style={{ fontSize: '12.5px', color: 'var(--ink-soft)', margin: '8px 0' }}>
                {kind === 'template_conformance'
                  ? `${candidates.length} section${candidates.length === 1 ? '' : 's'} `
                    + `in the template ${candidates.length === 1 ? 'is' : 'are'} missing`
                  : (
                    <>
                      {candidates.length} match{candidates.length === 1 ? '' : 'es'}
                      {scan.top_similarity != null
                        && `, closest at ${Math.round(scan.top_similarity * 100)}% similarity`}
                      {scan.duplicate_count > 0
                        && ` — ${scan.duplicate_count} above the duplicate threshold`}
                    </>
                  )}
              </div>

              {scan.summary && (
                <div style={{ fontSize: '13.5px', marginBottom: '8px' }}>{scan.summary}</div>
              )}

              <ul style={{ margin: 0, padding: 0 }}>
                {candidates.map((candidate, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MatchRow key={candidate.document_id || `redacted-${index}`} candidate={candidate} />
                ))}
              </ul>

              {scan.recommendations.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>
                    Recommendations
                  </div>
                  <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                    {scan.recommendations.map((item, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index} style={{ fontSize: '13.5px' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {scan.redacted_count > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '8px' }}>
              {scan.redacted_count} further match
              {scan.redacted_count === 1 ? ' is' : 'es are'} in locations you cannot access
              and {scan.redacted_count === 1 ? 'is' : 'are'} not named here.
            </div>
          )}

          {scan.error_detail && (
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '8px' }}>
              The matches above are complete. No written explanation was available:{' '}
              {scan.error_detail}
            </div>
          )}

          {!scan.has_narrative && candidates.length > 0 && !scan.error_detail && (
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '8px' }}>
              Matches found by the application. No language model has commented on them.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CorpusScanPanel;
